import React from 'react';
import styled from 'styled-components';
import * as CONSTANTS from '../constants';
import Link from 'next/link';
import { ModalContext } from '../context/modal';
import { logEvent } from '../utils/analytics';

const PLACEHOLDER = 'https://d3alngem7je9z2.cloudfront.net/screenshots/lazyyy.png';

const TimeAgo = styled.span`
  display: block;
  width: 100%;
  text-align: center;
  font-weight: 200;
  font-size: 10px;
  color: ${CONSTANTS.mediumGray};
`;

const EntryLink = styled.a`
  display: inline-block;
  width: 100%;
  height: 100%;
  background-color: #fff;
  border-radius: 3px;
`;

const Screenshot = styled.img`
  width: 100%;
  height: auto;
  display: block;
  pointer-events: none;
  border: 1px solid ${CONSTANTS.lightGray};
  border-radius: 3px;
`;

const Where = styled.span`
  display: none;
  position: absolute;
  margin-top: -32px;
  padding: 10px 20px;
  font-size: 18px;
  color: ${CONSTANTS.white};
  width: 100%;
  text-align: center;
  /*pointer-events: none;*/
  top: 50%;
  transform: translateY(-50%);
`;

const LinkChoice = styled.a`
  background-color: #2f2f2f;
  margin: 0 0.5px;
  width: 80px;
  color: ${CONSTANTS.white};
  font-size: 13px;
  display: none;
  text-align: center;
  padding: 9px 0;
  font-weight: 200;
  border-radius: 3px;

  &:hover {
    background-color: ${CONSTANTS.fakeCtaOpacity};
  }
`;

const Title = styled.span`
  color: #3f3f3f;
  font-weight: 200;
  font-size: ${CONSTANTS.headingTiny};
  display: block;
  text-align: center;
  margin-top: 15px;
  border-radius: 3px;

  &:hover {
    color: #888;
  }
`;

const AllEntries = styled.section`
  ${CONSTANTS.entryWrapStyles}

  ${Title} {
    ${
      props => props.darkMode === true ? `color: ${CONSTANTS.darkModeWhite}`
        : ''};
    };
  }

  ${LinkChoice} {
    ${
      props => props.darkMode === true ? `background-color: #1a1a1a;color: #fff; &:hover {background-color: ${CONSTANTS.darkModeCtaHoverBackground}`
        : ''};
    };
  }

  ${EntryLink} {
    ${
      props => props.darkMode === true ? `background-color: ${CONSTANTS.darkGray};}`
        : ''};
    };
  }

  ${Screenshot} {
    ${
      props => props.darkMode === true ? `border: 1px solid ${CONSTANTS.darkModeAppleGray};`
        : ''};
    };
  }

  @media (max-width: 1045px) {
    width: 100%;
  };

  @media (min-width: 1280px) {
    width: 1045px;
  };

  @media (min-width: 1580px) {
    width: 1560px;
  };

  @media (min-width: 2101px) {
    width: 2081px;
  };
`;

const Entry = styled.div`
  width: 500px;
  display: inline-table;
  margin: 10px 10px 30px 10px;
  position: relative;

  &:hover {
    ${Screenshot} {
      opacity: 0.1;
    }

    ${Where} {
      display: block;
    }

    ${LinkChoice} {
      display: inline-block;
    }
  }

  @media (max-width: 1045px) {
    width: 50%;
    height: auto;
    margin: 0;
    padding: 10px 10px 30px 10px;
  }

  @media (max-width: 740px) {
    margin: 10px auto 30px auto;
    padding: 0;
    display: block;
    width: auto;
    height: auto;
    max-width: 85%;
    max-height: auto;
  }
`;

export default class Entries extends React.PureComponent {
  constructor(props) {
    super(props);

    // Get a date object for the current time
    const d = new Date();
    // Set it to one month ago
    d.setMonth(d.getMonth() - 11);
    // Zero the hours
    d.setHours(0, 0, 0);
    // Zero the milliseconds
    d.setMilliseconds(0);
    // Get the time value in milliseconds and convert to seconds
    const threeMonthsAgo = (d/1000|0);

    this.state = {
      threeMonthsAgo,
    }

    this.convertPostTime = this.convertPostTime.bind(this);
    this.lazyImage = this.lazyImage.bind(this);
    this.showEntryDetails = this.showEntryDetails.bind(this);
  }

  lazyImage(e) {
    event.persist();

    let imageLoader = new Image();
    imageLoader.src = e.target.dataset.realsrc;

    imageLoader.onload = function(){
      e.target.src = e.target.dataset.realsrc;
    };
  }

  convertPostTime(entryPostDate, currentTimeStamp) {
    const templates = {
    prefix:  '',
    suffix:  ' ago',
    seconds: 'Less than a minute',
    minute:  'About a minute',
    minutes: '%d minutes',
    hour:    'About an hour',
    hours:   'About %d hours',
    day:     'a day',
    days:    '%d days',
    month:   'About a month',
    months:  '%d months',
    year:    'About a year',
    years:   '%d years'
    };

    const template = function (t, n) {
    return templates[t] && templates[t].replace(/%d/i, Math.abs(Math.round(n)));
    };

    const timer = function (time) {
      if (!time) return;
      time = new Date(time * 1000 || time);

      let now = new Date(currentTimeStamp * 1000 ||  currentTimeStamp);
      let seconds = ((now - time) * .001) >> 0;
      let minutes = seconds / 60;
      let hours = minutes / 60;
      let days = hours / 24;
      let years = days / 365;

      // if older than 3 months, don't show the date ;-)
      if(days > (30 * 3)) {
        return null;
      }

      const timeString =
        templates.prefix +
        (
          ((seconds < 45) && template('seconds', seconds)) ||
          ((seconds < 90) && template('minute', 1)) ||
          ((minutes < 45) && template('minutes', minutes)) ||
          ((minutes < 90) && template('hour', 1)) ||
          ((hours < 24) && template('hours', hours)) ||
          ((hours < 42) && template('day', 1)) ||
          ((days < 30) && template('days', days)) ||
          ((days < 45) && template('month', 1)) ||
          ((days < 365) && template('months', days / 30)) ||
          ((years < 1.5) && template('year', 1)) ||
          template('years', years)
        )
        + templates.suffix;

      return timeString;
    };

    return timer(entryPostDate);
  }

  showEntryDetails(e, entry) {
    e.preventDefault();

    let tags = entry.tags.split(',');
    let shades = entry.shades.split(',');

    const shadesList = shades.map(shade =>{
      return <li><a href="#">{shade}</a></li>;
    });

    const tagsList = tags.map(tag =>{
      return <li><a href="#">{tag}</a></li>;
    });

    const entryModalData = {
      entryDetailsModalTitle: entry.title,
      entryDetailsModalImg: entry.screenshot,
      entryDetailsModalTags: entry.tags,
      entryDetailsModalShades: entry.shades,
      entryDetailsModalOutobundUrl: entry.outboundUrl,
      entryDetailsModalUrlTitle: entry.urlTitle,
      entryDetailsInboundUrl: entry.inboundUrl,
    }

    this.context.updateValue('modalData', entryModalData);
    this.context.updateValue('currentModal', 'entry-details');
  }

  render() {
    const allEntries = this.props.entries.map((entry) => {
      const timeStamp = this.convertPostTime(entry.timestamp, this.props.currentTime);
      console.log(timeStamp);
      const timeAgoTag = timeStamp && (<TimeAgo>{timeStamp}</TimeAgo>)
      return (
        <Entry key={entry.outboundUrl}>
          <EntryLink href={entry.outboundUrl} onClick={(e) => {logEvent('entry | outbound', 'click from entry screenshot')}} target="_blank" rel="noopener noreferrer">
            <Screenshot src={entry.screenshot} alt={`Screenshot of ${entry.title}`}/>
          </EntryLink>
          <Link
            href={`/sites?slug=${entry.inboundUrl}`}
            as={`${entry.inboundUrl}`}
          >
            <a href={`/sites?slug=${entry.inboundUrl}`} onClick={(e) => {logEvent('entry | inbound', 'click from entry title')}}><Title>{entry.title}</Title></a>
          </Link>
          {timeAgoTag}
          <Where>
            <LinkChoice href={entry.inboundUrl} onClick={(e) => {this.showEntryDetails(e, entry);logEvent('entry | inbound', 'click from entry hover button');}}>Details</LinkChoice>
            <LinkChoice href={entry.outboundUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => {logEvent('entry | outbound', 'click from entry hover button')}}>Visit</LinkChoice>
          </Where>
        </Entry>
      )
    });

    return (
      <AllEntries darkMode={this.context.darkMode}>
        {allEntries}
      </AllEntries>
    );
  }
}

Entries.contextType = ModalContext;
